import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Envelope, ArrowLeft, PaperPlaneTilt, CircleNotch, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Reusable logo component: Mater (gray) + Match (orange)
// variant='light' for dark backgrounds (white Mater), 'default' for light backgrounds (gray Mater)
const BrandLogo = ({ size = 'md', variant = 'default', className = '' }) => {
  const imgClass = size === 'lg' ? 'h-20 w-auto' : size === 'sm' ? 'h-8 w-auto' : 'h-12 w-auto';
  const textClass = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-lg' : 'text-2xl';
  const materColor = variant === 'light' ? 'text-white' : 'text-zinc-600 dark:text-zinc-300';
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <img src="/matermatch-logo.png" alt="MaterMatch" className={imgClass} />
      <div>
        <span className={`${textClass} font-black ${materColor} tracking-tight`}>Mater</span>
        <span className={`${textClass} font-black text-orange-500 tracking-tight`}>Match</span>
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }
    
    setLoading(true);
    
    try {
      await authAPI.forgotPassword(email.trim());
      setSent(true);
      toast.success('Código enviado a tu correo');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.detail || 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.');
        return;
      }
      toast.info('Si el correo existe, recibirás un código de verificación');
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/reset-password', { state: { email } });
  };

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBtYXRlcmlhbHN8ZW58MHx8fHwxNzc1MDY1MDIwfDA&ixlib=rb-4.1.0&q=85"
          alt="Construction materials"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 to-zinc-950/40 flex items-center justify-center">
          <div className="text-white p-12">
            <BrandLogo size="lg" variant="light" className="mb-6" />
            <p className="text-lg text-zinc-300 max-w-md mt-4">
              Recupera el acceso a tu cuenta de forma segura
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-zinc-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <BrandLogo size="md" />
          </div>

          <Link to="/login" className="inline-flex items-center gap-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio de sesión
          </Link>

          {!sent ? (
            <>
              <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">¿Olvidaste tu contraseña?</h2>
              <p className="text-zinc-600 dark:text-zinc-400 mb-8">
                Ingresa tu correo electrónico y te enviaremos un código de verificación para restablecer tu contraseña.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <div className="relative">
                    <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 rounded-none"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary rounded-none h-12"
                  disabled={loading}
                  data-testid="send-code-btn"
                >
                  {loading ? (
                    <CircleNotch className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <PaperPlaneTilt className="w-5 h-5 mr-2" />
                      Enviar Código
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" weight="fill" />
                </div>
                <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">¡Código Enviado!</h2>
                <p className="text-zinc-600 dark:text-zinc-400 mb-2">
                  Hemos enviado un código de 6 dígitos a:
                </p>
                <p className="font-medium text-zinc-900 dark:text-zinc-100 mb-6">{email}</p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-8">
                  El código expira en 15 minutos. Revisa tu bandeja de entrada y spam.
                </p>

                <Button 
                  onClick={handleContinue}
                  className="w-full btn-primary rounded-none h-12"
                  data-testid="continue-btn"
                >
                  Continuar
                </Button>

                <button 
                  onClick={() => setSent(false)}
                  className="mt-4 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                >
                  ¿No recibiste el código? Intentar de nuevo
                </button>
              </div>
            </>
          )}

          <p className="text-center mt-8 text-zinc-600 dark:text-zinc-400">
            ¿Recordaste tu contraseña?{' '}
            <Link 
              to="/login" 
              className="text-zinc-900 dark:text-zinc-200 font-semibold hover:underline"
            >
              Iniciar Sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }
    
    if (!code.trim() || code.length !== 6) {
      toast.error('Por favor ingresa el código de 6 dígitos');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setLoading(true);
    
    try {
      await authAPI.resetPassword({
        email: email.trim(),
        code: code.trim(),
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success('¡Contraseña actualizada exitosamente!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al restablecer la contraseña';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" weight="fill" />
          </div>
          <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">¡Contraseña Actualizada!</h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-8">
            Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <Link to="/login">
            <Button className="w-full btn-primary rounded-none h-12" data-testid="go-to-login-btn">
              Ir a Iniciar Sesión
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBtYXRlcmlhbHN8ZW58MHx8fHwxNzc1MDY1MDIwfDA&ixlib=rb-4.1.0&q=85"
          alt="Construction materials"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 to-zinc-950/40 flex items-center justify-center">
          <div className="text-white p-12">
            <BrandLogo size="lg" variant="light" className="mb-6" />
            <p className="text-lg text-zinc-300 max-w-md mt-4">
              Crea una nueva contraseña segura
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-zinc-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <BrandLogo size="md" />
          </div>

          <Link to="/forgot-password" className="inline-flex items-center gap-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>

          <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">Restablecer Contraseña</h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-8">
            Ingresa el código de verificación y tu nueva contraseña.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none"
                required
                data-testid="reset-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Código de Verificación</Label>
              <Input
                id="code"
                type="text"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="rounded-none text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                required
                data-testid="reset-code-input"
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Ingresa el código de 6 dígitos enviado a tu correo</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Nueva Contraseña</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-none"
                required
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repite tu contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-none"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary rounded-none h-12"
              disabled={loading}
              data-testid="reset-password-btn"
            >
              {loading ? (
                <CircleNotch className="w-5 h-5 animate-spin" />
              ) : (
                'Restablecer Contraseña'
              )}
            </Button>
          </form>

          <p className="text-center mt-8 text-zinc-600 dark:text-zinc-400">
            ¿Recordaste tu contraseña?{' '}
            <Link 
              to="/login" 
              className="text-zinc-900 dark:text-zinc-200 font-semibold hover:underline"
            >
              Iniciar Sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
